/**
 * upload/ReviewStage.tsx — stage 3: everything the builder is about to publish, plus the one question
 * that gates submission.
 *
 * THE REVIEW QUESTION IS NOT NEW POLICY. Will's flow asks whether the tool needs review because of
 * the data it touches. The product already owns that question — `UP-F23`, the data-safety
 * attestation: a single required tick whose wording is fixed in the requirements, recorded with the
 * person and the time. So this stage asks exactly that, in exactly those words, rather than inventing
 * a second governance concept that would then disagree with the reviewer's checklist (`UP-F57`).
 *
 * Answering "this tool does touch such data" does NOT silently block the builder — it tells them
 * plainly that it cannot be shared here and why, which is what the guardrail is for (GC-7: submission
 * is blocked if unticked).
 *
 * A SECOND, NON-BLOCKING tick (RESPONSIBILITY_STATEMENT) lets the builder vouch for the tool ("does
 * not need AI SWAT team verification"). It is RECORDED but no longer decides the route: the platform
 * owner's `moderation_mode` setting does that server-side (UP-F20/F22). In manual_review every
 * submission is held Pending for the reviewer queue; in self_moderated it publishes as Approved with
 * no human step. The route echoes the actual publication_status back, so the wizard tells the builder
 * where the tool landed rather than predicting it from the tick. Only the data-safety tick can block
 * submission; this one neither blocks nor routes.
 *
 * Presentation only — every value shown is passed in, and both Edit buttons just ask the overlay to
 * change stage. Returning to Review afterwards loses nothing because the overlay holds the state, not
 * this component.
 */
import type { ExplanationMap, FlowComponent } from '@trailblazer/shared';
import type { ToolRefData } from '../api';
import type { DraftDetails } from './draftStore';

/**
 * The exact wording of the attestation, from UP-F23. Kept as a named constant because the backend's
 * `app.attestation.statement_version` column records "the exact wording ticked" — when that recording
 * is wired up, it must record THIS string, not a paraphrase someone retyped.
 */
export const ATTESTATION_STATEMENT =
  'I confirm this tool touches no secret data and stays inside our approved corporate boundary.';

/**
 * The builder's optional self-vouch. Ticking it means "I stand behind this tool, it does not need the
 * AI SWAT team to verify it". It is a recorded statement of responsibility — it does NOT decide the
 * publication route and does NOT skip any check. Whether a submission is held for review or published
 * at once is the platform owner's `moderation_mode` setting, applied server-side (UP-F20/F22).
 *
 * This tick NEVER blocks submission either — the data-safety attestation above is the hard gate.
 * Exported as a named constant for the same reason as ATTESTATION_STATEMENT: keeping the wording in
 * one place stops a paraphrase drifting in over time.
 */
export const RESPONSIBILITY_STATEMENT =
  'I take responsibility for this tool, it does not need AI SWAT team verification.';

export interface ReviewStageProps {
  details: DraftDetails;
  refData: ToolRefData;
  components: FlowComponent[];
  explanations: ExplanationMap;
  attested: boolean;
  /** The self-vouch tick (RESPONSIBILITY_STATEMENT). Recorded only; the platform's moderation_mode,
   *  not this tick, decides whether the tool is reviewed or published at once (UP-F20/F22). */
  takesResponsibility: boolean;
  /** True once Upload has been attempted, so the attestation can be flagged if still unticked. */
  showErrors: boolean;
  onAttestChange: (attested: boolean) => void;
  onResponsibilityChange: (takesResponsibility: boolean) => void;
  onEditDetails: () => void;
  onEditInstructions: () => void;
  /** Jump straight to one component's explanation on the Instructions stage. */
  onEditComponent: (componentId: string) => void;
}

/** Resolve a governed-list id to its label for display. */
function labelOf(options: { id: number; label: string }[], id: number): string {
  return options.find((o) => o.id === id)?.label ?? '—';
}

export function ReviewStage(props: ReviewStageProps) {
  const { details, refData, components, explanations } = props;

  return (
    <div>
      <p style={{ marginTop: 0 }}>
        Check this over before you share it. Everything below will be visible to colleagues looking
        for a tool like yours.
      </p>

      <section className="tb-review-sec" aria-labelledby="tb-review-details">
        <h3 id="tb-review-details">
          Details
          <button type="button" onClick={props.onEditDetails}>
            Edit details
          </button>
        </h3>
        <dl>
          <dt>Name</dt>
          <dd>{details.name}</dd>
          <dt>What it does</dt>
          <dd>{details.description}</dd>
          <dt>Problem it solves</dt>
          <dd>{details.problemSolved}</dd>
          <dt>Category</dt>
          <dd>{labelOf(refData.categories, details.categoryId)}</dd>
          <dt>Who may use it</dt>
          <dd>{labelOf(refData.accessRequirements, details.accessId)}</dd>
          <dt>Department</dt>
          <dd>{labelOf(refData.departments, details.departmentId)}</dd>
          <dt>Time saved per use</dt>
          <dd>{details.estimatedTimeSaved} minutes</dd>
          <dt>Depends on</dt>
          <dd>{details.dependencies}</dd>
          <dt>Connector setup</dt>
          <dd style={{ whiteSpace: 'pre-wrap' }}>
            {details.requirements.trim() || <span className="tb-muted">Not provided</span>}
          </dd>
          <dt>Tags</dt>
          <dd>
            {details.tags.length > 0 ? (
              details.tags.join(', ')
            ) : (
              <span className="tb-muted">None</span>
            )}
          </dd>
        </dl>
      </section>

      <section className="tb-review-sec" aria-labelledby="tb-review-instructions">
        <h3 id="tb-review-instructions">
          Setup instructions — {components.length} {components.length === 1 ? 'step' : 'steps'}
          <button type="button" onClick={props.onEditInstructions}>
            Edit instructions
          </button>
        </h3>
        {/* The same order and hierarchy as the Instructions stage and the stored record. */}
        {components.map((component) => (
          <div
            key={component.id}
            className="tb-review-step"
            style={{ marginLeft: `${Math.min(component.depth, 5) * 0.9}rem` }}
          >
            <strong>
              {component.numbering}
              {component.branch ? ` — ${component.branch}` : ''} — {component.label}
            </strong>
            <button
              type="button"
              className="tb-chip-remove"
              style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }}
              onClick={() => props.onEditComponent(component.id)}
              aria-label={`Edit the explanation for step ${component.numbering}, ${component.label}`}
            >
              edit
            </button>
            {component.typeLabel || component.connector ? (
              <span className="tb-comp-meta">
                {[component.typeLabel, component.connector, component.operation]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            ) : null}
            <p>{explanations[component.id]?.trim()}</p>
          </div>
        ))}
      </section>

      <section className="tb-review-sec" aria-labelledby="tb-review-data">
        <h3 id="tb-review-data">Data safety</h3>
        <p style={{ marginTop: 0 }}>
          Every tool shared here is checked against the same rule about the data it touches.
        </p>
        <label
          className="tb-field"
          style={{ display: 'flex', gap: '0.55rem', alignItems: 'start' }}
        >
          <input
            type="checkbox"
            checked={props.attested}
            onChange={(e) => props.onAttestChange(e.target.checked)}
            style={{ width: 'auto', marginTop: '0.2rem' }}
            aria-invalid={props.showErrors && !props.attested ? true : undefined}
            aria-describedby={props.showErrors && !props.attested ? 'tb-attest-err' : undefined}
          />
          <span style={{ fontWeight: 400 }}>{ATTESTATION_STATEMENT}</span>
        </label>
        {props.showErrors && !props.attested ? (
          <span className="tb-err" id="tb-attest-err">
            Confirm the data-safety statement to share this tool. If it does touch secret data, or
            reaches outside the approved boundary, it cannot be shared here — talk to the platform
            owner first.
          </span>
        ) : (
          <span className="tb-field-hint">
            Your confirmation is recorded with your name and the time. A reviewer checks it before
            the tool is approved.
          </span>
        )}
      </section>

      <section className="tb-review-sec" aria-labelledby="tb-review-responsibility">
        <h3 id="tb-review-responsibility">Verification</h3>
        <p style={{ marginTop: 0 }}>
          You can vouch for this tool by taking responsibility for it. Whether a tool is checked by
          the AI SWAT team before it goes live is a platform setting — we&rsquo;ll tell you where
          this one landed once you share it.
        </p>
        <label
          className="tb-field"
          style={{ display: 'flex', gap: '0.55rem', alignItems: 'start' }}
        >
          <input
            type="checkbox"
            checked={props.takesResponsibility}
            onChange={(e) => props.onResponsibilityChange(e.target.checked)}
            style={{ width: 'auto', marginTop: '0.2rem' }}
          />
          <span style={{ fontWeight: 400 }}>{RESPONSIBILITY_STATEMENT}</span>
        </label>
        <span className="tb-field-hint">
          Ticking this records that you stand behind the tool. It doesn&rsquo;t skip any checks the
          platform requires — those are set by the platform owner, not by this tick.
        </span>
      </section>
    </div>
  );
}
